/** StageLoader — ステージ JSON ローダー(Phase A2 Day 7)。
 *  Reference: docs/STAGE_DESIGN.md §6.1 (JSON 仕様)
 *  - shared_assets/stages 配下の stage_*.json を build-time に eager 読み込み
 *  - StageJson 型でランタイム検証
 *  - StageState 生成は呼び出し側(main.ts)に委譲
 *
 * PM 統合TODO (Lot 3 受け渡し):
 * - GameplayScreen.ts は stageState.hasMaskVictory() が true の場合、
 *   territory.contains() を使って mask_cells の塗布率を計算し、
 *   stageState.setMaskProgress(progress) を呼ぶ必要がある
 * - レンダリング:mask_cells の輪郭を薄い色で描画(まだ塗ってないところを可視化)
 */

export type StageDifficulty = 'easy' | 'medium' | 'hard' | 'super_hard';

export interface StageMetadata {
  readonly title: string;
  readonly description: string;
  readonly designer: string;
  readonly created_at: string;
  readonly iterations?: number;
}

/** GridPos 互換の軽量座標型(StageLoader 内部で完結させるため types.ts には依存しない)。 */
export interface MaskCellPos {
  readonly x: number;
  readonly y: number;
}

export interface VictoryCondition {
  /** 'coverage_min': 全マスに対する塗布率閾値(既存・全ステージ共通)。
   *  'composition_mask': mask_cells のうち塗布済み割合がvalue以上でクリア。
   *  string: 将来拡張のため後方互換として残す。
   */
  readonly type: 'coverage_min' | 'composition_mask' | string;
  /** coverage_min では塗布率(0..1)、composition_mask では mask 塗布割合(0..1)。 */
  readonly value: number;
  /** composition_mask 専用: 輪郭を構成するセル座標リスト。他 type では undefined。 */
  readonly mask_cells?: readonly MaskCellPos[];
}

export interface VictoryConditions {
  readonly primary: VictoryCondition;
  readonly secondary?: VictoryCondition;
}

export interface FailureConditions {
  readonly trail_cut_count?: number | null;
  readonly time_exceeded?: number | null;
  readonly moves_exceeded?: number | null;
  readonly lives_zero?: boolean;
}

export interface StageConstraints {
  readonly time_limit_sec: number | null;
  readonly move_limit: number | null;
  readonly trail_cut_limit: number | null;
}

export interface StageRewards {
  readonly color_base: number;
  readonly color_3star_bonus: number;
  readonly first_clear_pigment: number;
}

export interface StartingTerritoryRect {
  readonly type: 'rect';
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export type EnemyAi =
  | 'random_walk'
  | 'player_track'
  | 'trail_target'
  | 'sleeper'
  | 'patroller'
  | 'guard'
  | 'mirror_echo';

export interface EnemySpec {
  readonly id: string;
  readonly type: string;
  readonly spawn: { readonly x: number; readonly y: number };
  readonly ai: EnemyAi;
  readonly speed?: number;
  readonly direction_change_chance?: number;
  /** sleeper: 起動半径(デフォルト 5) */
  readonly wake_radius?: number;
  /** patroller: 巡回 waypoint リスト */
  readonly patrol_path?: readonly { readonly x: number; readonly y: number }[];
  /** guard: 守備中心座標 */
  readonly guard_anchor?: { readonly x: number; readonly y: number };
}

/** Obstacle:島内の進入不可セル矩形(壁=Dried Paint)。プレイヤー進入で fail trigger or block。
 *  G2: type='color_gate' → coverage_threshold 到達で通行可
 *  G3: type='sliding_wall' → path/period_sec で往復動作
 */
export interface ObstacleRect {
  readonly id?: string;
  readonly type: 'wall' | 'rect' | 'color_gate' | 'sliding_wall';
  readonly shape?: 'rect';
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** 'block' = 停止のみ、'fail' = ゲームオーバー(default 'fail') */
  readonly behavior?: 'block' | 'fail';
  /** color_gate: この coverage 比率以上で通行可(0.0..1.0) */
  readonly coverage_threshold?: number;
  /** sliding_wall: 巡回 waypoint 配列 */
  readonly path?: readonly { readonly x: number; readonly y: number }[];
  /** sliding_wall: 全 path 1 周の秒数 */
  readonly period_sec?: number;
}

export type CollectibleType =
  | 'color_bonus'
  | 'heart_bonus'
  | 'time_crystal'
  | 'shield_bubble'
  | 'coverage_token'
  | 'quick_dash'
  | 'art_note'
  | 'hourglass'
  | 'magnet_pigment'
  | 'painting_fragment';

/** Collectible:取得で報酬を得るアイテム(Paint Jar / 金箔 等)。 */
export interface Collectible {
  readonly id: string;
  readonly type: CollectibleType;
  readonly x: number;
  readonly y: number;
  readonly value?: number;
  /** art_note: ArtNotes 辞書のキー */
  readonly note_id?: string;
}

export type ZoneType =
  | 'speed_boost'
  | 'slow_zone'
  | 'wet_paint'
  | 'honey_trap'
  | 'wind_stream'
  | 'color_pool'
  | 'mirror_canvas'
  | 'ripple_zone';

/** SpecialZone:矩形ゾーンに侵入中だけ効果が適用される地形。 */
export interface SpecialZone {
  readonly id?: string;
  readonly type: ZoneType;
  readonly shape?: 'rect';
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** 速度乗数。speed_boost は 1.0 < n ≤ 1.8、slow_zone/wet_paint/honey_trap は 0.3 ≤ n < 1.0 を想定。 */
  readonly multiplier?: number;
  /** wind_stream: 押し流し方向 */
  readonly direction?: 'up' | 'down' | 'left' | 'right';
}

/** G1: Teleporter ペア — a に入ると b へ瞬間移動(逆も同様)、クールダウン 1.5 秒。 */
export interface TeleporterSpec {
  readonly id?: string;
  readonly a: { readonly x: number; readonly y: number };
  readonly b: { readonly x: number; readonly y: number };
}

export interface StageMap {
  readonly size: { readonly width: number; readonly height: number };
  readonly starting_position: { readonly x: number; readonly y: number };
  readonly starting_territory: StartingTerritoryRect;
  readonly obstacles: readonly ObstacleRect[];
  readonly enemies: readonly EnemySpec[];
  readonly collectibles: readonly Collectible[];
  readonly special_zones: readonly SpecialZone[];
  /** G1: ワープペア(省略可) */
  readonly teleporters?: readonly TeleporterSpec[];
}

export interface BalanceTargets {
  readonly expected_fail_rate: number;
  readonly expected_avg_attempts: number;
  readonly expected_almost_win_failure_ratio: number;
  readonly expected_hopeless_loss_ratio: number;
}

export interface StageJson {
  readonly stage_id: string;
  readonly chapter: number;
  readonly level_in_chapter: number;
  readonly stage_type: string;
  readonly difficulty: StageDifficulty;
  readonly world_id: string;
  readonly metadata: StageMetadata;
  readonly victory_conditions: VictoryConditions;
  readonly failure_conditions: FailureConditions;
  readonly constraints: StageConstraints;
  readonly rewards: StageRewards;
  readonly map: StageMap;
  readonly balance_targets: BalanceTargets;
  /** ライバル領地の自動拡張間隔(秒)。未指定なら 3.0 秒。
   * PM 統合TODO: GameplayScreen の enableAutoExpand 引数に stageJson.rival_expand_interval_sec ?? 3.0 を渡す
   */
  readonly rival_expand_interval_sec?: number;
  readonly remote_config?: Record<string, unknown>;
}

const stageModules = import.meta.glob<{ default: unknown }>(
  '../../../shared_assets/stages/**/stage_*.json',
  { eager: true },
);

const stageById = new Map<string, StageJson>();
for (const [path, module] of Object.entries(stageModules)) {
  const json = (module as { default: unknown }).default;
  validateStageJson(json, path);
  const stage = json as StageJson;
  stageById.set(stage.stage_id, stage);
}

export class StageLoader {
  /** stageId(例: "1-01")でステージ JSON を取得。未存在なら例外。 */
  static load(stageId: string): StageJson {
    const stage = stageById.get(stageId);
    if (!stage) {
      const available = Array.from(stageById.keys()).join(', ');
      throw new Error(`Stage "${stageId}" not found. Available: [${available}]`);
    }
    return stage;
  }

  /** 全ステージ ID 一覧を返す(ステージマップ UI 用、Day 11)。 */
  static listStageIds(): string[] {
    return Array.from(stageById.keys()).sort();
  }

  /** 全ステージ JSON を返す(QA / 自動プレイテスト用、Day 10)。 */
  static listStages(): StageJson[] {
    return Array.from(stageById.values());
  }
}

function validateStageJson(json: unknown, path: string): void {
  if (typeof json !== 'object' || json === null) {
    throw new Error(`Invalid stage JSON at ${path}: not an object`);
  }
  const obj = json as Record<string, unknown>;
  const required = [
    'stage_id',
    'chapter',
    'level_in_chapter',
    'stage_type',
    'difficulty',
    'map',
    'victory_conditions',
  ];
  for (const key of required) {
    if (!(key in obj)) {
      throw new Error(`Invalid stage JSON at ${path}: missing field "${key}"`);
    }
  }
  const map = obj.map as Record<string, unknown>;
  if (
    typeof map !== 'object' ||
    map === null ||
    !('size' in map) ||
    !('starting_position' in map) ||
    !('starting_territory' in map)
  ) {
    throw new Error(`Invalid stage JSON at ${path}: malformed map`);
  }
}
